import React, { useState, memo } from 'react';
import { X, AlertCircle, CheckCircle2, Info } from 'lucide-react';
import { Button } from './ui/Button';
interface SelfHelpModalProps {
  isOpen: boolean;
  onClose: () => void;
}
const pgsiQuestions = ['Have you bet more than you could really afford to lose?', 'Have you needed to gamble with larger amounts of money to get the same feeling of excitement?', 'When you gambled, did you go back another day to try to win back the money you lost?', 'Have you borrowed money or sold anything to get money to gamble?', 'Have you felt that you might have a problem with gambling?', 'Has gambling caused you any health problems, including stress or anxiety?', 'Have people criticized your betting or told you that you had a gambling problem, regardless of whether or not you thought it was true?', 'Has your gambling caused any financial problems for you or your household?', 'Have you felt guilty about the way you gamble or what happens when you gamble?'];
const pgsiOptions = [{
  label: 'Never',
  value: 0
}, {
  label: 'Sometimes',
  value: 1
}, {
  label: 'Often',
  value: 2
}, {
  label: 'Almost always',
  value: 3
}];
const dsmQuestions = ['Do you need to gamble with increasing amounts of money in order to achieve the desired excitement?', 'Are you restless or irritable when attempting to cut down or stop gambling?', 'Have you made repeated unsuccessful efforts to control, cut back, or stop gambling?', 'Are you often preoccupied with gambling (e.g., having persistent thoughts of reliving past gambling experiences, handicapping or planning the next venture, thinking of ways to get money with which to gamble)?', 'Do you often gamble when feeling distressed (e.g., helpless, guilty, anxious, depressed)?', 'After losing money gambling, do you often return another day to get even ("chasing" one\'s losses)?', 'Do you lie to conceal the extent of involvement with gambling?', 'Have you jeopardized or lost a significant relationship, job, or educational or career opportunity because of gambling?', 'Do you rely on others to provide money to relieve desperate financial situations caused by gambling?'];
export function SelfHelpModal({
  isOpen,
  onClose
}: SelfHelpModalProps) {
  const [pgsiAnswers, setPgsiAnswers] = useState<(number | null)[]>(Array(9).fill(null));
  const [showPgsiResult, setShowPgsiResult] = useState(false);
  const [dsmAnswers, setDsmAnswers] = useState<(number | null)[]>(Array(9).fill(null));
  const [showDsmResult, setShowDsmResult] = useState(false);
  if (!isOpen) return null;
  const handlePgsiAnswer = (index: number, value: number) => {
    const newAnswers = [...pgsiAnswers];
    newAnswers[index] = value;
    setPgsiAnswers(newAnswers);
    setShowPgsiResult(false);
  };
  const handleDsmAnswer = (index: number, value: number) => {
    const newAnswers = [...dsmAnswers];
    newAnswers[index] = value;
    setDsmAnswers(newAnswers);
    setShowDsmResult(false);
  };
  const pgsiAnsweredCount = pgsiAnswers.filter(a => a !== null).length;
  const dsmAnsweredCount = dsmAnswers.filter(a => a !== null).length;
  const getPgsiResult = () => {
    const score = pgsiAnswers.reduce((a, b) => (a || 0) + (b || 0), 0) as number;
    if (score === 0) return {
      level: 'Non-problem gambler',
      color: 'text-green-600',
      bg: 'bg-green-50',
      border: 'border-green-200'
    };
    if (score <= 2) return {
      level: 'Low level of problems with few or no identified negative consequences.',
      color: 'text-yellow-600',
      bg: 'bg-yellow-50',
      border: 'border-yellow-200'
    };
    if (score <= 7) return {
      level: 'Moderate level of problems leading to some negative consequences.',
      color: 'text-orange-600',
      bg: 'bg-orange-50',
      border: 'border-orange-200'
    };
    return {
      level: 'Problem gambling with negative consequences and a possible loss of control.',
      color: 'text-red-600',
      bg: 'bg-red-50',
      border: 'border-red-200'
    };
  };
  const getDsmResult = () => {
    const score = dsmAnswers.reduce((a, b) => (a || 0) + (b || 0), 0) as number;
    if (score <= 3) return {
      level: 'No gambling disorder criteria met.',
      color: 'text-green-600',
      bg: 'bg-green-50',
      border: 'border-green-200'
    };
    if (score <= 5) return {
      level: 'Mild gambling disorder.',
      color: 'text-yellow-600',
      bg: 'bg-yellow-50',
      border: 'border-yellow-200'
    };
    if (score <= 7) return {
      level: 'Moderate gambling disorder.',
      color: 'text-orange-600',
      bg: 'bg-orange-50',
      border: 'border-orange-200'
    };
    return {
      level: 'Severe gambling disorder.',
      color: 'text-red-600',
      bg: 'bg-red-50',
      border: 'border-red-200'
    };
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" onClick={onClose} data-id="element-508">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-509" />

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto flex flex-col" onClick={e => e.stopPropagation()} data-id="element-510">
        {/* Header */}
        <div className="sticky top-0 bg-white/95 backdrop-blur-md border-b border-gray-100 px-6 py-5 flex items-center justify-between z-20" data-id="element-511">
          <h2 className="text-2xl md:text-3xl font-serif font-bold text-[#0A5E5C]" data-id="element-512">
            Self-Help and Helping Loved Ones
          </h2>
          <button onClick={onClose} className="w-10 h-10 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors flex-shrink-0" data-id="element-513">
            <X className="w-6 h-6" data-id="element-514" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 md:p-10 space-y-12 text-gray-700 leading-relaxed" data-id="element-515">
          {/* Intro */}
          <section className="space-y-6" data-id="element-516">
            <p className="text-lg" data-id="element-517">
              This section is created for those who want to better understand
              their own gambling behavior and for those who are trying to
              understand what gambling is and why it can carry risks, as well as
              for those who want to assess the possible risks of gambling and
              get practical self-control tools.
            </p>
            <p data-id="element-518">
              Gambling can be a form of entertainment and bring pleasure.
              However, sometimes, when a person loses control for certain
              reasons, gambling can start to negatively affect their finances,
              emotional state, relationships with loved ones, and quality of
              life.
            </p>
            <p data-id="element-519">
              Therefore, it is very important to be attentive to yourself, to
              your own emotions, feelings, reactions, and states that accompany
              you before, during, and after the game. It is very important to be
              in touch with yourself and be aware of everything that happens to
              you when you play, when you think about the game. Perhaps you use
              the game to escape from an unbearable reality that you don't like
              for some reason. Perhaps you feel incredible emotional excitement
              during the game that is nowhere else available to you in life.
              Perhaps you decided that gambling is an easy way to make money. Or
              perhaps all these thoughts and feelings are present in you at the
              same time.
            </p>
            <div className="bg-[#F5F0E8] p-6 rounded-xl border-l-4 border-[#D4A843]" data-id="element-520">
              <p className="font-medium text-[#0A5E5C]" data-id="element-521">
                Do not turn away from them and do not judge yourself, show a
                little attention to yourself and if you are reading this text,
                then you are definitely capable and ready to regain control over
                your game and your life. Believe in yourself. Only you truly
                decide who and what controls you and your life, and even if, due
                to certain circumstances, you gave control over yourself to the
                game, or to something else that captured you, you can definitely
                take this control back. This is your absolute right, the right
                to control yourself and your life, and even if it seems
                impossible to you now, just at least allow such a thought and
                try to move forward and the space of life will begin to provide
                you with support and help on the way back to yourself. We
                believe in you and your strength.
              </p>
            </div>
          </section>

          {/* Tests Intro */}
          <section className="space-y-6" data-id="element-522">
            <h3 className="text-2xl font-serif font-bold text-[#0A5E5C] border-b pb-2" data-id="element-523">
              Self-Assessment Tests for Gambling Addiction Risk
            </h3>
            <div className="flex items-start gap-3 bg-blue-50 p-5 rounded-xl border border-blue-100" data-id="element-524">
              <Info className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-525" />
              <div data-id="element-526">
                <h4 className="font-bold text-blue-900 mb-1" data-id="element-527">Important</h4>
                <p className="text-blue-800 text-sm" data-id="element-528">
                  The tests below are only tools for self-diagnosis. They are
                  not a medical diagnosis and cannot replace a consultation with
                  a psychologist, psychotherapist, or other qualified
                  specialist. If the test results cause concern or you feel that
                  gambling is starting to affect your life, it is recommended to
                  seek professional help.
                </p>
              </div>
            </div>
          </section>

          {/* PGSI Test */}
          <section className="space-y-8 bg-gray-50 p-6 md:p-8 rounded-2xl border border-gray-200" data-id="element-529">
            <div data-id="element-530">
              <h3 className="text-2xl font-serif font-bold text-[#0A5E5C] mb-3" data-id="element-531">
                Problem Gambling Severity Index (PGSI)
              </h3>
              <p className="mb-4" data-id="element-532">
                PGSI is an internationally recognized tool for assessing the
                risk of problem gambling behavior.
              </p>
              <ul className="text-sm text-gray-600 space-y-1 mb-6 bg-white p-4 rounded-lg border border-gray-100" data-id="element-533">
                <li data-id="element-534">
                  <strong data-id="element-535">Developed by:</strong> Julie Ferris and Harold Wynne
                  (2001)
                </li>
                <li data-id="element-536">
                  <strong data-id="element-537">As part of:</strong> Canadian Problem Gambling Index
                  (CPGI)
                </li>
                <li data-id="element-538">
                  <strong data-id="element-539">Commissioned by:</strong> Canadian Centre on Substance
                  Abuse
                </li>
              </ul>
              <p className="font-medium text-[#0A5E5C]" data-id="element-540">
                Instructions: Answer each question by assessing your behavior
                over the past 12 months.
              </p>
            </div>

            <div className="space-y-8" data-id="element-541">
              {pgsiQuestions.map((q, idx) => <div key={idx} className="bg-white p-5 rounded-xl shadow-sm border border-gray-100" data-id="element-542">
                  <p className="font-medium mb-4 text-gray-800" data-id="element-543">
                    <span className="text-[#D4A843] mr-2" data-id="element-544">{idx + 1}.</span>
                    {q}
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-545">
                    {pgsiOptions.map(opt => <button key={opt.label} onClick={() => handlePgsiAnswer(idx, opt.value)} className={`py-2 px-3 rounded-lg text-sm font-medium transition-all border ${pgsiAnswers[idx] === opt.value ? 'bg-[#0A5E5C] text-white border-[#0A5E5C] shadow-md' : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-[#0A5E5C] hover:bg-white'}`} data-id="element-546">
                        {opt.label}
                      </button>)}
                  </div>
                </div>)}
            </div>

            <div className="flex flex-col items-center pt-6 border-t border-gray-200" data-id="element-547">
              <p className="text-sm text-gray-500 mb-4" data-id="element-548">
                Answer all questions to see your result ({pgsiAnsweredCount} of
                9)
              </p>
              <Button variant="primary" size="lg" disabled={pgsiAnsweredCount < 9} onClick={() => setShowPgsiResult(true)} className="min-w-[200px]" data-id="element-549">
                See Results
              </Button>

              {showPgsiResult && <div className={`mt-8 p-6 rounded-xl border w-full text-center animate-fade-in ${getPgsiResult().bg} ${getPgsiResult().border}`} data-id="element-550">
                  <h4 className="text-lg font-medium text-gray-700 mb-2" data-id="element-551">
                    Your Result:
                  </h4>
                  <p className={`text-2xl font-bold ${getPgsiResult().color}`} data-id="element-552">
                    {getPgsiResult().level}
                  </p>
                  <p className="text-sm mt-4 text-gray-600" data-id="element-553">
                    Score:{' '}
                    {pgsiAnswers.reduce((a, b) => (a || 0) + (b || 0), 0)} / 27
                  </p>
                </div>}
            </div>
          </section>

          {/* DSM-5 Test */}
          <section className="space-y-8 bg-gray-50 p-6 md:p-8 rounded-2xl border border-gray-200" data-id="element-554">
            <div data-id="element-555">
              <h3 className="text-2xl font-serif font-bold text-[#0A5E5C] mb-3" data-id="element-556">
                DSM‑5 Gambling Disorder Criteria
              </h3>
              <p className="mb-4" data-id="element-557">
                These criteria are used for clinical assessment of a gambling
                disorder. In this edition, gambling addiction was officially
                classified under the category of behavioral addictions.
              </p>
              <ul className="text-sm text-gray-600 space-y-1 mb-6 bg-white p-4 rounded-lg border border-gray-100" data-id="element-558">
                <li data-id="element-559">
                  <strong data-id="element-560">Developer:</strong> American Psychiatric Association
                </li>
                <li data-id="element-561">
                  <strong data-id="element-562">Year:</strong> 2013
                </li>
                <li data-id="element-563">
                  <strong data-id="element-564">Source:</strong> Diagnostic and Statistical Manual of
                  Mental Disorders, Fifth Edition (DSM‑5)
                </li>
              </ul>
              <p className="font-medium text-[#0A5E5C]" data-id="element-565">
                Instructions: Answer Yes or No, assessing your condition over
                the last 12 months.
              </p>
            </div>

            <div className="space-y-6" data-id="element-566">
              {dsmQuestions.map((q, idx) => <div key={idx} className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-567">
                  <p className="font-medium text-gray-800 flex-grow" data-id="element-568">
                    <span className="text-[#D4A843] mr-2" data-id="element-569">{idx + 1}.</span>
                    {q}
                  </p>
                  <div className="flex gap-3 flex-shrink-0" data-id="element-570">
                    <button onClick={() => handleDsmAnswer(idx, 1)} className={`py-2 px-6 rounded-lg text-sm font-medium transition-all border ${dsmAnswers[idx] === 1 ? 'bg-[#0A5E5C] text-white border-[#0A5E5C] shadow-md' : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-[#0A5E5C] hover:bg-white'}`} data-id="element-571">
                      Yes
                    </button>
                    <button onClick={() => handleDsmAnswer(idx, 0)} className={`py-2 px-6 rounded-lg text-sm font-medium transition-all border ${dsmAnswers[idx] === 0 ? 'bg-[#0A5E5C] text-white border-[#0A5E5C] shadow-md' : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-[#0A5E5C] hover:bg-white'}`} data-id="element-572">
                      No
                    </button>
                  </div>
                </div>)}
            </div>

            <div className="flex flex-col items-center pt-6 border-t border-gray-200" data-id="element-573">
              <p className="text-sm text-gray-500 mb-4" data-id="element-574">
                Answer all questions to see your result ({dsmAnsweredCount} of
                9)
              </p>
              <Button variant="primary" size="lg" disabled={dsmAnsweredCount < 9} onClick={() => setShowDsmResult(true)} className="min-w-[200px]" data-id="element-575">
                See Results
              </Button>

              {showDsmResult && <div className={`mt-8 p-6 rounded-xl border w-full text-center animate-fade-in ${getDsmResult().bg} ${getDsmResult().border}`} data-id="element-576">
                  <h4 className="text-lg font-medium text-gray-700 mb-2" data-id="element-577">
                    Your Result:
                  </h4>
                  <p className={`text-2xl font-bold ${getDsmResult().color}`} data-id="element-578">
                    {getDsmResult().level}
                  </p>
                  <p className="text-sm mt-4 text-gray-600" data-id="element-579">
                    Criteria met:{' '}
                    {dsmAnswers.reduce((a, b) => (a || 0) + (b || 0), 0)} / 9
                  </p>
                </div>}
            </div>
          </section>

          {/* Signs of Problem Gambling */}
          <section className="space-y-6" data-id="element-580">
            <h3 className="text-2xl font-serif font-bold text-[#0A5E5C] border-b pb-2" data-id="element-581">
              What are the signs of problem gambling?
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-582">
              <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm" data-id="element-583">
                <h4 className="font-bold text-[#D4A843] mb-4 text-lg" data-id="element-584">
                  Behavioral signs
                </h4>
                <ul className="space-y-2" data-id="element-585">
                  <li className="flex items-start gap-2" data-id="element-586">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#0A5E5C] mt-2 flex-shrink-0" data-id="element-587" />{' '}
                    constant thoughts about gambling
                  </li>
                  <li className="flex items-start gap-2" data-id="element-588">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#0A5E5C] mt-2 flex-shrink-0" data-id="element-589" />{' '}
                    desire to increase bets
                  </li>
                  <li className="flex items-start gap-2" data-id="element-590">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#0A5E5C] mt-2 flex-shrink-0" data-id="element-591" />{' '}
                    attempts to win back lost money
                  </li>
                  <li className="flex items-start gap-2" data-id="element-592">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#0A5E5C] mt-2 flex-shrink-0" data-id="element-593" />{' '}
                    difficulty stopping the game
                  </li>
                  <li className="flex items-start gap-2" data-id="element-594">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#0A5E5C] mt-2 flex-shrink-0" data-id="element-595" />{' '}
                    hiding gambling activity
                  </li>
                </ul>
              </div>
              <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm" data-id="element-596">
                <h4 className="font-bold text-[#D4A843] mb-4 text-lg" data-id="element-597">
                  Emotional signs
                </h4>
                <ul className="space-y-2" data-id="element-598">
                  <li className="flex items-start gap-2" data-id="element-599">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#0A5E5C] mt-2 flex-shrink-0" data-id="element-600" />{' '}
                    feeling guilty after playing
                  </li>
                  <li className="flex items-start gap-2" data-id="element-601">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#0A5E5C] mt-2 flex-shrink-0" data-id="element-602" />{' '}
                    irritability or anxiety when unable to play
                  </li>
                  <li className="flex items-start gap-2" data-id="element-603">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#0A5E5C] mt-2 flex-shrink-0" data-id="element-604" />{' '}
                    using gambling to escape from stress or problems
                  </li>
                  <li className="flex items-start gap-2" data-id="element-605">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#0A5E5C] mt-2 flex-shrink-0" data-id="element-606" />{' '}
                    feeling a loss of control
                  </li>
                </ul>
              </div>
              <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm" data-id="element-607">
                <h4 className="font-bold text-[#D4A843] mb-4 text-lg" data-id="element-608">
                  Financial signs
                </h4>
                <ul className="space-y-2" data-id="element-609">
                  <li className="flex items-start gap-2" data-id="element-610">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#0A5E5C] mt-2 flex-shrink-0" data-id="element-611" />{' '}
                    spending beyond one's means
                  </li>
                  <li className="flex items-start gap-2" data-id="element-612">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#0A5E5C] mt-2 flex-shrink-0" data-id="element-613" />{' '}
                    loans or credits for gambling
                  </li>
                  <li className="flex items-start gap-2" data-id="element-614">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#0A5E5C] mt-2 flex-shrink-0" data-id="element-615" />{' '}
                    debts or financial difficulties
                  </li>
                </ul>
              </div>
              <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm" data-id="element-616">
                <h4 className="font-bold text-[#D4A843] mb-4 text-lg" data-id="element-617">
                  Social signs
                </h4>
                <ul className="space-y-2" data-id="element-618">
                  <li className="flex items-start gap-2" data-id="element-619">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#0A5E5C] mt-2 flex-shrink-0" data-id="element-620" />{' '}
                    conflicts with family
                  </li>
                  <li className="flex items-start gap-2" data-id="element-621">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#0A5E5C] mt-2 flex-shrink-0" data-id="element-622" />{' '}
                    problems at work
                  </li>
                  <li className="flex items-start gap-2" data-id="element-623">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#0A5E5C] mt-2 flex-shrink-0" data-id="element-624" />{' '}
                    social isolation
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Where to start? */}
          <section className="space-y-6" data-id="element-625">
            <h3 className="text-2xl font-serif font-bold text-[#0A5E5C] border-b pb-2" data-id="element-626">
              Where to start?
            </h3>
            <p data-id="element-627">
              If gambling really affects your life and you are ready to admit
              it, or at least entertain the thought, this is already a
              successful start. After all, recognition and awareness require
              great strength, and if you have this strength to admit the current
              state of affairs in your life, you definitely have the strength to
              act to help yourself. It is very important to give this awareness
              space and not try to fight it or yourself. Try to explore with
              curiosity when and how you might have lost control of yourself and
              given it to the game, under what circumstances this happened, what
              exactly you felt and what could be the reason for such a decision,
              whether it was conscious, or you don't remember how you suddenly
              realized that you couldn't stop and didn't find the strength in
              yourself to admit it immediately.
            </p>
            <p data-id="element-628">
              If you have someone very close and safe for you, someone who
              accepts you and does not judge, you could share all these
              thoughts, memories, and feelings that arise in you during such an
              exploration. A kind, accepting look from a close, safe person can
              help a lot. Think about it, maybe there is a person nearby to whom
              you could open up and not be alone in this exploration of
              yourself.
            </p>
          </section>

          {/* What can you do to help yourself? */}
          <section className="space-y-6" data-id="element-629">
            <h3 className="text-2xl font-serif font-bold text-[#0A5E5C] border-b pb-2" data-id="element-630">
              What can you do to help yourself?
            </h3>
            <p data-id="element-631">
              If you have decided to take control of your gambling, and believe
              that it is enough for you to simply increase your awareness of the
              gambling process, then the following simple but effective tips can
              help you.
            </p>

            <div className="space-y-4 mt-6" data-id="element-632">
              {[{
              title: 'Set limits for yourself.',
              desc: 'Determine the amount you can afford to spend on entertainment and do not exceed it.'
            }, {
              title: 'Control your playing time.',
              desc: 'Set time limits or use a timer. Remember: on legal sites, you cannot be allowed to play without setting such limits yourself. Be mindful about setting these limits.'
            }, {
              title: "Don't try to chase losses.",
              desc: 'Attempts to win back lost money often lead to even greater losses.'
            }, {
              title: 'Avoid gambling under emotional influence.',
              desc: 'Stress, alcohol, or strong emotions can lead to impulsive decisions.'
            }, {
              title: 'Take regular breaks.',
              desc: 'Regular pauses help maintain control.'
            }].map((tip, idx) => <div key={idx} className="flex gap-4 items-start" data-id="element-633">
                  <div className="w-8 h-8 rounded-full bg-[#D4A843]/20 text-[#D4A843] flex items-center justify-center font-bold flex-shrink-0 mt-1" data-id="element-634">
                    {idx + 1}
                  </div>
                  <div data-id="element-635">
                    <h4 className="font-bold text-gray-800" data-id="element-636">{tip.title}</h4>
                    <p className="text-gray-600" data-id="element-637">{tip.desc}</p>
                  </div>
                </div>)}
            </div>

            <div className="bg-[#F5F0E8] p-6 rounded-xl mt-8" data-id="element-638">
              <p data-id="element-639">
                During such pauses, try to analyze your behavior during the
                game, your general emotional state, and your emotional state
                directly during the game. Ask yourself if gambling is truly just
                entertainment for you, bringing positive emotions without any
                negative impacts on your financial state, emotional state, your
                thoughts, mood, your relationships with the outside world and
                the people around you. Try to do this analysis on a regular
                basis.
              </p>
            </div>
          </section>

          {/* Self-exclusion */}
          <section className="space-y-6" data-id="element-640">
            <p className="font-medium text-lg" data-id="element-641">
              If you have realized that gambling negatively affects your life,
              mood, finances, relationships, thoughts, and have decided to act
              to change this, the following steps may be useful for you.
            </p>

            <div className="space-y-8 mt-6" data-id="element-642">
              <div className="flex gap-4 items-start" data-id="element-643">
                <div className="w-10 h-10 rounded-full bg-[#0A5E5C] text-white flex items-center justify-center font-bold flex-shrink-0 text-lg" data-id="element-644">
                  1
                </div>
                <div className="space-y-3" data-id="element-645">
                  <h4 className="font-bold text-xl text-[#0A5E5C]" data-id="element-646">
                    Self-exclusion
                  </h4>
                  <p data-id="element-647">
                    Submit an application for self-exclusion through the website
                    of the State Agency "Playcity"{' '}
                    <a href="https://www.playcity.gov.ua" target="_blank" rel="noopener noreferrer" className="text-[#D4A843] hover:underline" data-id="element-648">
                      www.playcity.gov.ua
                    </a>{' '}
                    in the "For Citizens" section there is an option
                    "Application for restriction", or you can submit such an
                    application on the website of any online gambling operator.
                  </p>
                  <p data-id="element-649">
                    As a result of submitting such an application, your data
                    will be entered into the Register of persons whose access to
                    gambling establishments and/or participation in gambling is
                    restricted (hereinafter – the Register).
                  </p>
                  <p data-id="element-650">
                    But you must realize that entering your data into the
                    Register will restrict your access to gambling only with
                    legal gambling operators. Therefore, you can use additional
                    tools to help yourself and consciously restrict your access
                    to gambling.
                  </p>
                </div>
              </div>

              <div className="flex gap-4 items-start" data-id="element-651">
                <div className="w-10 h-10 rounded-full bg-[#0A5E5C] text-white flex items-center justify-center font-bold flex-shrink-0 text-lg" data-id="element-652">
                  2
                </div>
                <div className="space-y-3" data-id="element-653">
                  <h4 className="font-bold text-xl text-[#0A5E5C]" data-id="element-654">
                    BETBLOCKER
                  </h4>
                  <p data-id="element-655">
                    Use the free blocking tool BETBLOCKER, which you can learn
                    about on the website{' '}
                    <a href="https://betblocker.org" target="_blank" rel="noopener noreferrer" className="text-[#D4A843] hover:underline" data-id="element-656">
                      https://betblocker.org
                    </a>{' '}
                    or download it as a mobile app.
                  </p>
                  <p data-id="element-657">
                    This tool can be installed for various types of devices and
                    operating systems and it operates from the territory of
                    Ukraine, all information on the site and in the application
                    is in Ukrainian.
                  </p>
                </div>
              </div>

              <div className="flex gap-4 items-start" data-id="element-658">
                <div className="w-10 h-10 rounded-full bg-[#0A5E5C] text-white flex items-center justify-center font-bold flex-shrink-0 text-lg" data-id="element-659">
                  3
                </div>
                <div className="space-y-3" data-id="element-660">
                  <h4 className="font-bold text-xl text-[#0A5E5C]" data-id="element-661">Gamban</h4>
                  <p data-id="element-662">
                    Use another blocking tool Gamban{' '}
                    <a href="https://gamban.com" target="_blank" rel="noopener noreferrer" className="text-[#D4A843] hover:underline" data-id="element-663">
                      https://gamban.com
                    </a>
                  </p>
                  <p data-id="element-664">
                    This tool operates in Ukraine, but does not have a
                    Ukrainian-language version and it is paid (there is a free
                    trial version).
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Helping loved ones */}
          <section id="what-to-do-if-someone-close" className="space-y-6 pt-8 border-t border-gray-200" data-id="element-665">
            <h3 className="text-2xl md:text-3xl font-serif font-bold text-[#0A5E5C] mb-6" data-id="element-666">
              What to do if someone close to you gambles uncontrollably
            </h3>
            <p data-id="element-667">
              When a loved one faces problem gambling behavior, it can cause
              anxiety, anger, confusion, or powerlessness. It is important to
              realize your emotions and reactions, and try to compose yourself
              and believe that your support and attention can really help if you
              yourself are conscious and calm. It is also important to act
              carefully. Try to start by simply having a calm conversation so
              that the person, who may not yet admit the real state of affairs,
              feels that you are ready to help them without judgment or
              accusations. Your sincere interest and attention to your loved
              one, their emotional state and experiences in their life, your
              support and love can become a fulcrum where your loved one can
              make a decision to admit the problem and choose to act with your
              support. At the same time, do not neglect attention to yourself
              and your own emotional state. Take care of yourself and your
              resources.
            </p>

            <h4 className="font-bold text-xl text-[#D4A843] mt-8 mb-6" data-id="element-668">
              Some tips that may help you
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-669">
              {[{
              title: 'Try to understand the problem',
              desc: 'Gambling addiction is a complex behavioral problem that can be associated with stress, emotional difficulties, or a desire to avoid problems. Try to understand your loved one who gambles uncontrollably, what they are avoiding, what stress could be the reason for plunging into uncontrolled gambling. If the person is really close to you and you are familiar with their life, you will be able to understand the circumstances of their life that could have caused them to lose control over gambling.'
            }, {
              title: 'Speak calmly and without accusations',
              desc: 'Talk to your loved one. Try to build conversations in such a way as to create an atmosphere of trust and acceptance of the situation. Under no circumstances should you judge, blame, or pity. Try to gently invite the person to calmly explore together the state in which the person is, show a sincere interest in what they feel when they play and what prevents them from stopping, what they avoid by plunging into the game, what they lack in real life, why they return to the game and cannot stop. Explore this together and do not judge anything you discover. Such a dialogue, calm discussion, and exchange of thoughts can launch the process of awareness in the person of their problem gambling behavior and become the beginning of real positive changes.'
            }, {
              title: 'Do not try to control everything',
              desc: 'Excessive control can lead to conflicts or hiding the problem.'
            }, {
              title: 'Do not cover debts',
              desc: 'Paying off debts or constant financial assistance can only delay solving the problem.'
            }, {
              title: 'Encourage seeking help',
              desc: 'You can suggest contacting a psychologist, counselor, or support groups. The main thing is not to pressure and try to wait until the person comes to such a decision independently and consciously.'
            }, {
              title: 'Support positive changes',
              desc: 'Even small steps — for example, reducing playing time — can be important. Make a plan of small steps together and try to provide support in following it, giving support and your attention.'
            }, {
              title: 'Help find alternative activities',
              desc: 'Sports, hobbies, travel, or learning can help replace the time and attention that the game previously occupied. Try to encourage the person to experience new things from new activities, communication, travel, etc., which can captivate them and give positive emotions and positive experiences.'
            }, {
              title: 'Set your own boundaries',
              desc: "It is important to determine what is unacceptable to you. Do not give money for gambling and do not take financial responsibility for other people's debts. This also applies to your emotional state."
            }, {
              title: 'Take care of yourself',
              desc: 'Living next to a person with an addiction can be emotionally difficult, so it is important not to forget about your own well-being. Helping and supporting a loved one should not devastate you. You will not be able to fully help someone if you yourself have lost your emotional balance. Take care of yourself.'
            }, {
              title: 'Remember that changes take time',
              desc: 'Overcoming problem behavior can take time, but the support of loved ones helps to move forward.'
            }].map((tip, idx) => <div key={idx} className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm flex flex-col" data-id="element-670">
                  <div className="flex items-center gap-3 mb-3" data-id="element-671">
                    <div className="w-8 h-8 rounded-full bg-[#0A5E5C]/10 text-[#0A5E5C] flex items-center justify-center font-bold flex-shrink-0" data-id="element-672">
                      {idx + 1}
                    </div>
                    <h5 className="font-bold text-gray-800" data-id="element-673">{tip.title}</h5>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed" data-id="element-674">
                    {tip.desc}
                  </p>
                </div>)}
            </div>
          </section>

          {/* Legal Protection */}
          <section className="space-y-6 bg-[#0A5E5C] text-white p-6 md:p-10 rounded-2xl" data-id="element-675">
            <h3 className="text-2xl font-serif font-bold text-[#D4A843] mb-4" data-id="element-676">
              If there is a threat to the financial security of your family due
              to the uncontrolled gambling of your loved one (relative)
            </h3>
            <p className="text-white/90" data-id="element-677">
              If your loved one (relative) does not admit the existence of
              problems with gambling due to some circumstances and does not show
              a desire to look for a way out to overcome it, and perhaps
              uncontrolled gambling threatens the financial security of your
              family, you can use some legal tools for protection.
            </p>
            <p className="text-white/90" data-id="element-678">
              Submit an Application to restrict such a person to the State
              Agency "PLAYCITY" through the website{' '}
              <a href="https://www.playcity.gov.ua" target="_blank" rel="noopener noreferrer" className="text-[#D4A843] hover:underline font-medium" data-id="element-679">
                www.playcity.gov.ua
              </a>{' '}
              in the "For Citizens" section there is an option "Application for
              restriction", or in written form. As a result of submitting such
              an application, the person will be entered into the Register of
              persons whose access to gambling establishments and/or
              participation in gambling is restricted (hereinafter – the
              Register).
            </p>

            <div className="bg-white/10 p-6 rounded-xl space-y-4 mt-6" data-id="element-680">
              <p className="font-medium text-[#D4A843]" data-id="element-681">
                Important requirements:
              </p>
              <ul className="space-y-3 text-sm text-white/90" data-id="element-682">
                <li className="flex items-start gap-2" data-id="element-683">
                  <CheckCircle2 className="w-5 h-5 text-[#D4A843] flex-shrink-0" data-id="element-684" />{' '}
                  You can submit such an application only regarding a
                  first-degree relative (parents, husband, wife, children
                  including adopted) or if you are the legal representative.
                </li>
                <li className="flex items-start gap-2" data-id="element-685">
                  <CheckCircle2 className="w-5 h-5 text-[#D4A843] flex-shrink-0" data-id="element-686" />{' '}
                  You must submit a document confirming your family relationship
                  (provide a copy certified with your signature).
                </li>
                <li className="flex items-start gap-2" data-id="element-687">
                  <CheckCircle2 className="w-5 h-5 text-[#D4A843] flex-shrink-0" data-id="element-688" />{' '}
                  An application can only be submitted regarding a person who is
                  21 years old. Persons under 21 cannot be allowed to gamble by
                  law.
                </li>
              </ul>
            </div>

            <div className="space-y-4 mt-6" data-id="element-689">
              <p className="font-medium text-[#D4A843]" data-id="element-690">
                The application must be accompanied by justification and
                documents confirming the need to establish a restriction due to
                one of the following circumstances:
              </p>
              <ul className="list-disc pl-5 space-y-2 text-white/90 text-sm" data-id="element-691">
                <li data-id="element-692">
                  exceeding gambling expenses over the person's personal income,
                  which puts such a person or their family in a difficult
                  financial situation;
                </li>
                <li data-id="element-693">
                  exceeding gambling expenses over the person's personal income,
                  which puts the player or their family in a difficult financial
                  situation, which is carried out using borrowed funds,
                  including on the terms of a financial loan;
                </li>
                <li data-id="element-694">
                  the presence of debt obligations in the amount of more than
                  100 subsistence minimums for citizens' incomes;
                </li>
                <li data-id="element-695">
                  evasion of the person from paying alimony for more than three
                  months;
                </li>
                <li data-id="element-696">
                  if the person or members of their family are recipients of a
                  housing subsidy or benefits for paying for housing and
                  communal services.
                </li>
              </ul>
            </div>

            <div className="bg-[#D4A843]/20 border border-[#D4A843]/30 p-5 rounded-xl mt-6" data-id="element-697">
              <p className="text-white/90" data-id="element-698">
                <strong className="text-[#D4A843]" data-id="element-699">It is important</strong> that
                by submitting such an application you can restrict the access of
                the specified person to gambling for a period of up to 6 months.
                To restrict access for a longer period, it is necessary to apply
                to the court.
              </p>
            </div>

            <p className="text-center text-white/80 pt-6" data-id="element-700">
              If you need more detailed information on this issue, please
              contact us by filling out the form on the site or write to our
              email.
            </p>
          </section>
        </div>
      </div>
    </div>;
}